import { useCallback, useEffect, useState } from 'react';
import { safeLocalStorageGetItem, safeLocalStorageSetItem } from '../utils/safeStorage';

const HIDDEN_STORAGE_KEY = 'forge-sidebar-hidden';
const COLLAPSED_WIDTH = 38;
const MIN_WIDTH = 38;
const MAX_WIDTH = 400;
const DEFAULT_EXPANDED_WIDTH = 138;

/**
 * @param expandedWidth what the sidebar opens and restores to. Forge asks for a
 * wider one because its destination names are what identify a page (DLI-1907);
 * the Archon and Eval shells keep the default.
 */
export function useSidebar(expandedWidth: number = DEFAULT_EXPANDED_WIDTH) {
  const [width, setWidth] = useState(expandedWidth);
  const [isHidden, setIsHidden] = useState(safeLocalStorageGetItem(HIDDEN_STORAGE_KEY) === 'true');
  const [isResizing, setIsResizing] = useState(false);

  const isCollapsed = width <= COLLAPSED_WIDTH;

  const toggleCollapse = useCallback(() => {
    if (isCollapsed) {
      setWidth(expandedWidth);
    } else {
      setWidth(COLLAPSED_WIDTH);
    }
  }, [isCollapsed, expandedWidth]);

  const hideSidebar = useCallback(() => {
    setIsHidden(true);
    safeLocalStorageSetItem(HIDDEN_STORAGE_KEY, 'true');
  }, []);

  const showSidebar = useCallback(() => {
    setWidth(expandedWidth);
    setIsHidden(false);
    safeLocalStorageSetItem(HIDDEN_STORAGE_KEY, 'false');
  }, [expandedWidth]);

  // Drag-to-resize logic
  const onDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    document.body.classList.add('is-resizing');

    const onMove = (ev: MouseEvent) => {
      let newWidth = ev.clientX;
      if (newWidth < MIN_WIDTH) newWidth = MIN_WIDTH;
      if (newWidth > MAX_WIDTH) newWidth = MAX_WIDTH;
      setWidth(newWidth);
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.classList.remove('is-resizing');
      setIsResizing(false);

      setWidth((current) => {
        const final = current <= COLLAPSED_WIDTH ? COLLAPSED_WIDTH : current;
        return final;
      });
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  // Sync body class
  useEffect(() => {
    if (isResizing) {
      document.body.classList.add('is-resizing');
    } else {
      document.body.classList.remove('is-resizing');
    }
    return () => document.body.classList.remove('is-resizing');
  }, [isResizing]);

  return {
    width,
    isCollapsed,
    isHidden,
    isResizing,
    toggleCollapse,
    hideSidebar,
    showSidebar,
    onDragStart,
  } as const;
}
